import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { asyncStatuses } from 'store/enum';

// Async thunk for logging in
export const loggingIn = createAsyncThunk(
  'user/login',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post('/user/login/', data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Async thunk for logging out
export const loggingOut = createAsyncThunk(
  'user/logout',
  async (data, { dispatch }) => {
    try {
      const response = await axios.post('/user/logout/', data, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.status === 204) {
        dispatch(logout());
      }
    } catch (err) {
      dispatch(logout());
      window.location.href = '/';
    }
  }
);

// Async thunk for checking if user has 2FA enabled
export const checkUserFor2FA = createAsyncThunk(
  'user/check2FA',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/user/check-2fa-status/', {
        email,
      });
      return {
        requires2FA: response.data.is_2fa_enabled,
        isAccountLocked: response.data.is_account_locked,
        email: response.data.email,
        firstName: response.data.first_name,
        lastName: response.data.last_name
      };
    } catch (err) {
      // Handle timeout and network errors
      if (err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        return rejectWithValue({ message: 'Connection timeout. Please check your network and try again.' });
      }
      return rejectWithValue(err.response?.data || { message: 'Failed to check 2FA status. Please try again.' });
    }
  }
);

// Async thunk for sending login OTP
export const sendLoginOTP = createAsyncThunk(
  'user/sendOTP',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post('/user/login-otp/', { email });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: 'Failed to send OTP' });
    }
  }
);

// Async thunk for verifying login OTP
export const verifyLoginOTP = createAsyncThunk(
  'user/verifyOTP',
  async ({ email, otp_code }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/user/verify-login-otp/', { email, otp_code });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: 'Failed to verify OTP' });
    }
  }
);

// Async thunk for resending OTP
export const resendOTP = createAsyncThunk(
  'user/resendOTP',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post('/user/resend-otp/', { email, otp_type: 'login' });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: 'Failed to resend OTP' });
    }
  }
);

// Async thunk for refreshing current user data (permissions, roles, etc.)
export const refreshUserData = createAsyncThunk(
  'user/refreshUserData',
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId = getState().auth?.user?.id;
      if (!userId) return rejectWithValue({ message: 'No user ID found' });
      const response = await axios.get(`/user/user/${userId}/`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: 'Failed to refresh user data' });
    }
  }
);

// Async thunk for checking profile lock status on page reload
export const checkProfileLockStatus = createAsyncThunk(
  'user/checkProfileLock',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/user/current-user-employee/');
      return response.data.profile_locked ?? false;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// User slice
const userSlice = createSlice({
  name: 'user',
  initialState: {
    user: null,
    token: null,
    error: null,
    status: null,
    organization: null,
    isAuthenticated: false,
    defaultRoute: null,
    requires2FA: false,
    isAccountLocked: false,
    otpSent: false,
    userEmail: null,
    userName: null,
    twoFactorStatus: null,
    profileLocked: false,
  },
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.organization = null;
      state.defaultRoute = null;
      state.requires2FA = false;
      state.isAccountLocked = false;
      state.otpSent = false;
      state.userEmail = null;
      state.userName = null;
      state.twoFactorStatus = null;
      state.profileLocked = false;
    },
    changeOrg(state, action){
      console.log(action.payload);
      state.organization = action.payload;
    },
    reset2FAState(state) {
      state.requires2FA = false;
      state.isAccountLocked = false;
      state.otpSent = false;
      state.userEmail = null;
      state.userName = null;
      state.twoFactorStatus = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loggingIn.pending, (state) => {
        state.status = asyncStatuses.LOADING;
        state.error = null;
      })
      .addCase(loggingIn.fulfilled, (state, action) => {
        state.status = asyncStatuses.SUCCESS;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.defaultRoute = action.payload.default_route || '/';
        state.profileLocked = action.payload.profile_locked ?? false;
        // Set organization from user data if available
        if (action.payload.user?.organization) {
          state.organization = action.payload.user.organization;
        }
      })
      .addCase(loggingIn.rejected, (state, action) => {
        state.status = asyncStatuses.FAILED;
        state.error = action.payload;
        state.isAuthenticated = false;
        if (action.payload?.status === 'ACCOUNT_LOCKED') {
          state.isAccountLocked = true;
        }
      })
      .addCase(loggingOut.fulfilled, (state) => {
        state.status = asyncStatuses.SUCCESS;
        state.isAuthenticated = false;
        state.user = null;
        state.organization = null;
      })
      // Check 2FA status reducers
      .addCase(checkUserFor2FA.pending, (state) => {
        state.twoFactorStatus = asyncStatuses.LOADING;
        state.error = null;
      })
      .addCase(checkUserFor2FA.fulfilled, (state, action) => {
        state.twoFactorStatus = asyncStatuses.SUCCESS;
        state.requires2FA = action.payload.requires2FA;
        state.isAccountLocked = action.payload.isAccountLocked;
        state.userEmail = action.payload.email;
        state.userName = `${action.payload.firstName || ''} ${action.payload.lastName || ''}`.trim();
      })
      .addCase(checkUserFor2FA.rejected, (state, action) => {
        state.twoFactorStatus = asyncStatuses.FAILED;
        state.error = action.payload;
      })
      // Send OTP reducers
      .addCase(sendLoginOTP.pending, (state) => {
        state.status = asyncStatuses.LOADING;
        state.error = null;
      })
      .addCase(sendLoginOTP.fulfilled, (state, action) => {
        state.status = asyncStatuses.SUCCESS;
        state.otpSent = true;
      })
      .addCase(sendLoginOTP.rejected, (state, action) => {
        state.status = asyncStatuses.FAILED;
        state.error = action.payload;
      })
      // Verify OTP reducers
      .addCase(verifyLoginOTP.pending, (state) => {
        state.status = asyncStatuses.LOADING;
        state.error = null;
      })
      .addCase(verifyLoginOTP.fulfilled, (state, action) => {
        state.status = asyncStatuses.SUCCESS;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.defaultRoute = action.payload.default_route || '/';
        state.profileLocked = action.payload.profile_locked ?? false;
        // Set organization from user data if available
        if (action.payload.user?.organization) {
          state.organization = action.payload.user.organization;
        }
        // Reset 2FA state after successful login
        state.requires2FA = false;
        state.otpSent = false;
        state.userEmail = null;
        state.userName = null;
        state.twoFactorStatus = null;
      })
      .addCase(verifyLoginOTP.rejected, (state, action) => {
        state.status = asyncStatuses.FAILED;
        state.error = action.payload;
        state.isAuthenticated = false;
        if (action.payload?.status === 'ACCOUNT_LOCKED') {
          state.isAccountLocked = true;
        }
      })
      // Resend OTP reducers
      .addCase(resendOTP.pending, (state) => {
        state.status = asyncStatuses.LOADING;
        state.error = null;
      })
      .addCase(resendOTP.fulfilled, (state, action) => {
        state.status = asyncStatuses.SUCCESS;
        state.otpSent = true;
      })
      .addCase(resendOTP.rejected, (state, action) => {
        state.status = asyncStatuses.FAILED;
        state.error = action.payload;
      })
      // Check profile lock status reducers
      .addCase(checkProfileLockStatus.fulfilled, (state, action) => {
        state.profileLocked = action.payload;
      })
      .addCase(checkProfileLockStatus.rejected, (state) => {
        // Keep current profileLocked state if API call fails
      })
      // Refresh user data reducers
      .addCase(refreshUserData.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      // Handle rehydration - reset loading states (must be after all addCase calls)
      .addMatcher(
        (action) => action.type === 'persist/REHYDRATE',
        (state, action) => {
          // After rehydration, always reset loading states
          if (action.payload?.auth) {
            const rehydratedAuth = action.payload.auth;
            // Preserve persisted data but reset loading states
            return {
              ...rehydratedAuth,
              status: null,
              twoFactorStatus: null,
              error: null,
            };
          }
        }
      );
  },
});

export const { logout, changeOrg, reset2FAState } = userSlice.actions;
export default userSlice.reducer;
